import { useCallback, useEffect, useRef, useState } from 'react';
import {
  mapStatusToState,
  normalizePurchaseStatus,
  PAYMENT_VERIFICATION_INTERVAL_MS,
  PAYMENT_VERIFICATION_MAX_ATTEMPTS,
  PurchaseVerificationState,
} from '../constants/purchaseSession';
import { generateEventId, fireMetaPixelEvent } from '../services/tracking.service';

interface VerifyResponse {
  status: string;
  pack_code: string;
  user_plan_id?: string;
}

interface VerificationResult {
  state: PurchaseVerificationState;
  status: string;
  packCode: string | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_SERVER_URL || '';

/**
 * Simplified hook to verify a purchase session.
 * 
 * For Berexia payments, the `token` query param from the redirect IS the session ID.
 * We poll the backend /verify endpoint which:
 * 1. Looks up the session in the database
 * 2. Calls the provider API to verify the transaction
 * 3. Activates the plan if successful (idempotent)
 * 4. Returns the final status
 */
export function usePurchaseSessionVerification(sessionId: string | null): VerificationResult {
  console.log('[Payment] usePurchaseSessionVerification initialized with sessionId:', sessionId);
  
  const [state, setState] = useState<PurchaseVerificationState>('pending');
  const [status, setStatus] = useState<string>('unknown');
  const [packCode, setPackCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const attemptsRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTerminalRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const verify = useCallback(async () => {
    console.log('[Payment] verify() called, attempt:', attemptsRef.current + 1, 'isTerminal:', isTerminalRef.current);
    
    // Don't run if already in terminal state
    if (isTerminalRef.current) {
      console.log('[Payment] Already in terminal state, skipping verify');
      return;
    }

    if (!sessionId) {
      console.log('[Payment] No sessionId provided');
      setState('failure');
      setError('No session ID provided');
      setIsLoading(false);
      isTerminalRef.current = true;
      return;
    }

    setIsLoading(true);
    attemptsRef.current += 1;

    const url = `${API_BASE_URL}/api/purchase-sessions/${sessionId}/verify`;
    console.log('[Payment] Fetching:', url);
    
    try {
      const response = await fetch(url);
      console.log('[Payment] Response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Session not found. Please contact support.');
        }
        throw new Error(`Verification failed (${response.status})`);
      }

      const data: VerifyResponse = await response.json();
      console.log('[Payment] API Response data:', data);
      
      const normalized = normalizePurchaseStatus(data.status);
      const derivedState = mapStatusToState(normalized);

      console.log('[Payment] Derived state:', { rawStatus: data.status, normalized, derivedState, packCode: data.pack_code });

      setStatus(normalized);
      setPackCode(data.pack_code || null);
      setState(derivedState);
      setError(null);

      // If terminal state (success or failure), stop polling
      if (derivedState === 'success' || derivedState === 'failure') {
        console.log('[Payment] Terminal state reached:', derivedState);
        // Fire Purchase pixel event on successful payment
        if (derivedState === 'success') {
          try {
            const purchaseEventId = generateEventId();
            fireMetaPixelEvent('Purchase', { currency: 'USD' }, purchaseEventId);
          } catch {
            // Silent fail — tracking should never break the app
          }
        }
        setIsLoading(false);
        isTerminalRef.current = true;
        clearTimer();
        return;
      }

      // If still pending, schedule another attempt
      if (attemptsRef.current < PAYMENT_VERIFICATION_MAX_ATTEMPTS) {
        console.log('[Payment] Scheduling next poll in', PAYMENT_VERIFICATION_INTERVAL_MS, 'ms');
        timeoutRef.current = setTimeout(verify, PAYMENT_VERIFICATION_INTERVAL_MS);
      } else {
        // Max attempts reached, show unknown/pending state
        console.log('[Payment] Max attempts reached, setting state to unknown');
        setState('unknown');
        setIsLoading(false);
        isTerminalRef.current = true;
      }

    } catch (err) {
      console.error('[Payment] Verification error:', err);
      const message = err instanceof Error ? err.message : 'Verification failed';
      setError(message);

      // Retry on error if under max attempts
      if (attemptsRef.current < PAYMENT_VERIFICATION_MAX_ATTEMPTS) {
        console.log('[Payment] Retrying after error, attempt', attemptsRef.current);
        timeoutRef.current = setTimeout(verify, PAYMENT_VERIFICATION_INTERVAL_MS);
      } else {
        console.log('[Payment] Max retry attempts reached after errors');
        setState('failure');
        setIsLoading(false);
        isTerminalRef.current = true;
      }
    }
  }, [sessionId, clearTimer]);

  const retry = useCallback(() => {
    attemptsRef.current = 0;
    isTerminalRef.current = false;
    setError(null);
    setState('pending');
    setIsLoading(true);
    verify();
  }, [verify]);

  // Start verification on mount - only once
  useEffect(() => {
    if (!sessionId) {
      setState('failure');
      setError('Missing session information');
      setIsLoading(false);
      return;
    }

    // Only start if not already in terminal state
    if (!isTerminalRef.current) {
      verify();
    }

    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return {
    state,
    status,
    packCode,
    isLoading,
    error,
    retry,
  };
}
